import React, { useState } from 'react';
import { Calculator, DollarSign, Anchor, Activity, Settings, LogOut, FolderOpen } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import CableConfigurator from './CableConfigurator';
import PriceCalculatorNew from './PriceCalculatorNew';
import MarineCableCalculator from './MarineCableCalculator';
import DualModeCableCalculator from './DualModeCableCalculator';
import PricingSettings, { DEFAULT_PRICING } from './PricingSettings';
import SavedQuotes from './SavedQuotes';
import { useAuth } from '../pages/AuthContext';

const MainApp = () => {
  const { user, signOut } = useAuth();
  const [activeMainTab, setActiveMainTab] = useState('configuration');
  const [activeYear, setActiveYear] = useState('2025');
  const [yearlyPricing, setYearlyPricing] = useState(() => JSON.parse(JSON.stringify(DEFAULT_PRICING)));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/agi-logo.jpeg" alt="AGI" className="h-10 object-contain" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Cable Config</h1>
                <p className="text-sm text-slate-600">Advanced Geosciences, Inc.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600 hidden sm:inline">{user?.email}</span>
              <button
                onClick={signOut}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Exit</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Tabs */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="space-y-6">
          <TabsList className="grid w-full max-w-5xl mx-auto grid-cols-6 h-12">
            <TabsTrigger 
              value="configuration" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1e3a8a] data-[state=active]:to-[#1e40af] data-[state=active]:text-white text-xs sm:text-sm"
            >
              <Calculator className="w-4 h-4 mr-1 sm:mr-2" />
              Cable Config
            </TabsTrigger>
            <TabsTrigger 
              value="pricing"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1e3a8a] data-[state=active]:to-[#1e40af] data-[state=active]:text-white text-xs sm:text-sm"
            >
              <DollarSign className="w-4 h-4 mr-1 sm:mr-2" />
              Passive Cable
            </TabsTrigger>
            <TabsTrigger 
              value="dualmode"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1e3a8a] data-[state=active]:to-[#1e40af] data-[state=active]:text-white text-xs sm:text-sm"
            >
              <Activity className="w-4 h-4 mr-1 sm:mr-2" />
              Dual Mode
            </TabsTrigger>
            <TabsTrigger 
              value="marine"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1e3a8a] data-[state=active]:to-[#1e40af] data-[state=active]:text-white text-xs sm:text-sm"
            >
              <Anchor className="w-4 h-4 mr-1 sm:mr-2" />
              Marine Cable
            </TabsTrigger>
            <TabsTrigger 
              value="settings"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white text-xs sm:text-sm"
            >
              <Settings className="w-4 h-4 mr-1 sm:mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger 
              value="saved"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-green-500 data-[state=active]:text-white text-xs sm:text-sm"
            >
              <FolderOpen className="w-4 h-4 mr-1 sm:mr-2" />
              Saved
            </TabsTrigger>
          </TabsList>

          <TabsContent value="configuration">
            <CableConfigurator />
          </TabsContent>

          <TabsContent value="pricing">
            <PriceCalculatorNew activeYear={activeYear} pricing={yearlyPricing[activeYear]} />
          </TabsContent>

          <TabsContent value="dualmode">
            <DualModeCableCalculator activeYear={activeYear} pricing={yearlyPricing[activeYear]} />
          </TabsContent>

          <TabsContent value="marine">
            <MarineCableCalculator activeYear={activeYear} pricing={yearlyPricing[activeYear]} />
          </TabsContent>

          <TabsContent value="settings">
            <PricingSettings
              activeYear={activeYear}
              setActiveYear={setActiveYear}
              yearlyPricing={yearlyPricing}
              setYearlyPricing={setYearlyPricing}
            />
          </TabsContent>

          <TabsContent value="saved">
            <SavedQuotes />
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="mt-16 bg-white/80 backdrop-blur-sm border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <p className="text-center text-slate-600 text-sm">
            Smart Cable Quote System | Based on Edwards 1977 Research | Advanced Geosciences Inc.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MainApp;
